import { startOfWeek, endOfDay, addWeeks, addMonths, addYears, startOfDay, differenceInCalendarDays } from "date-fns";

/**
 * durationDate.ts (InstaLOD GmbH)
 *
 * Copyright © 2021 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Etienne Daher, 2021
 *
 * @file durationDate.ts
 * @author Etienne Daher
 * @copyright 2021 InstaLOD GmbH. All rights reserved.
 * @section License
 */
export enum durations {
  ONE_DAY = 'One day',
  ONE_WEEK = 'One week',
  TWO_WEEKS = 'Two weeks',
  ONE_MONTH = 'One month',
  ONE_YEAR = 'One year',
  TWO_YEARS = 'Two years',
  THREE_YEARS = 'Three years',
  NINETYNINE_YEARS = 'Ninety-nine years'
}

export const definedDates = () => {
  return {
    startOfToday: startOfDay(new Date()),
    ONE_DAY: endOfDay(new Date()),
    ONE_WEEK: endOfDay(addWeeks(new Date(), 1)),
    TWO_WEEKS: endOfDay(addWeeks(new Date(), 2)),
    ONE_MONTH: endOfDay(addMonths(new Date(), 1)),
    ONE_YEAR: endOfDay(addYears(new Date(), 1)),
    TWO_YEARS: endOfDay(addYears(new Date(), 2)),
    THREE_YEARS: endOfDay(addYears(new Date(), 3)),
    NINETYNINE_YEARS: endOfDay(addYears(new Date(), 99))
  }
}
/**
 * The number of whole days a license that runs from `startDate` to `endDate` covers.
 *
 * Counted in calendar days rather than by dividing a millisecond difference, so a license that
 * starts at 09:00 and ends at 23:59 on the same day reads as one day and a DST boundary does not
 * shave an hour off the total.
 *
 * @param startDate The license start date.
 * @param endDate The license end date.
 * @returns The duration in days, or null when either date is missing or unparseable.
 */
export const licenseDurationInDays = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined
): number | null => {
  if (startDate == null || endDate == null) {
    return null;
  }

  const start: Date = new Date(startDate);
  const end: Date = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const days: number = differenceInCalendarDays(startOfDay(end), startOfDay(start));

  // A same-day license — what the `ONE_DAY` duration produces — crosses no calendar boundary but is
  // still a day long, so zero is reported as one. A negative result means the dates are inverted,
  // and is returned as-is rather than floored: a caller showing "1 day" there would hide the error.
  return days === 0 ? 1 : days;
};

/**
 * The number of days the given {@link durations} key spans, measured from today.
 *
 * Derived from {@link definedDates} rather than from a table of constants so the two can never
 * disagree: `ONE_MONTH` is however many days the *current* month has, which is what a license
 * created today from that template would actually get.
 *
 * @param duration A {@link durations} key, e.g. `ONE_YEAR`.
 * @returns The duration in days, or null when the key is not one this enum defines.
 */
export const durationKeyInDays = (duration: string): number | null => {
  const dates: Record<string, Date> = definedDates() as Record<string, Date>;
  const endDate: Date = dates[duration];

  if (endDate == null) {
    return null;
  }

  return licenseDurationInDays(dates.startOfToday, endDate);
};
